/**
 * Cleans what a visitor typed or pasted before it is checked or sent.
 *
 * `String.prototype.trim` removes ordinary and non-breaking spaces but not the
 * invisible characters that ride along when an address is copied from an email
 * signature, Outlook or a web page (zero-width spaces, direction marks, soft
 * hyphens). Those made a correct email fail our check with a message about
 * "accents" the visitor could not see (Oct 5, 2026: a visitor's email was
 * refused 11 times on two pages). Both the form and the server run this, so the
 * same value is checked on both sides and Salesforce receives the clean one.
 */

/** Zero-width and bidirectional marks, word joiners, BOM and soft hyphen. */
const INVISIBLE = /[­᠎​-‏‪-‮⁠-⁤﻿]/g;
/** Space-like characters other than a plain space (NBSP, thin spaces, ideographic). */
const ODD_SPACE = /[   -   　]/g;
/** Curly apostrophes and the prime some keyboards substitute for ' in names like O'Brien. */
const CURLY_APOSTROPHE = /[‘’ʼ′]/g;

/** Free text: invisible characters removed, odd spaces made plain, ends trimmed. */
export function cleanText(value: string): string {
  return value.replace(INVISIBLE, '').replace(ODD_SPACE, ' ').trim();
}

/**
 * An email address: cleaned like text, with every space removed (an address
 * never contains one), curly apostrophes straightened, commas after the @ made
 * dots, and a trailing period from copying the end of a sentence dropped.
 */
export function cleanEmail(value: string): string {
  const email = cleanText(value).replace(/\s+/g, '').replace(CURLY_APOSTROPHE, "'");
  return commasToDotsAfterAt(email).replace(/\.+$/, '');
}

/**
 * A comma is never valid after the @, and on phone and iPad keyboards it sits
 * next to the period: "name@company,com" was refused on Oct 5 (an iPad visitor
 * tried twice and left). After the @ only, so a local part stays as typed.
 */
function commasToDotsAfterAt(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 0) return email;
  return email.slice(0, at + 1) + email.slice(at + 1).replace(/,+/g, '.');
}

/** The visitor-typed text fields of the lead forms. */
const TEXT_FIELDS = ['firstName', 'lastName', 'name', 'company', 'phone', 'eventCity', 'message'] as const;
const EMAIL_FIELD = 'email';

/** A copy of a submission with every visitor-typed string field cleaned; other values untouched. */
export function cleanSubmissionFields<T extends Record<string, unknown>>(fields: T): T {
  const out: Record<string, unknown> = { ...fields };
  for (const key of TEXT_FIELDS) {
    const value = out[key];
    if (typeof value === 'string') out[key] = cleanText(value);
  }
  const email = out[EMAIL_FIELD];
  if (typeof email === 'string') out[EMAIL_FIELD] = cleanEmail(email);
  return out as T;
}

/** Longest shape reported, so a pasted essay does not flood the event. */
export const VALUE_SHAPE_MAX = 80;

/**
 * The shape of a value without its content: letters become "a", digits "9",
 * everything else (punctuation, spaces, @, accents) is kept, and any character
 * outside printable ASCII is written as ⟨U+XXXX⟩. "jane@acme,com" reads
 * "aaaa@aaaa,aaa", which shows the comma typo but not the address.
 */
export function valueShape(value: string): string {
  let shape = '';
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (/[A-Za-z]/.test(char)) shape += 'a';
    else if (/[0-9]/.test(char)) shape += '9';
    else if (code >= 0x20 && code < 0x7f) shape += char;
    else shape += `⟨U+${code.toString(16).toUpperCase().padStart(4, '0')}⟩`;
    if (shape.length >= VALUE_SHAPE_MAX) return `${shape.slice(0, VALUE_SHAPE_MAX)}…`;
  }
  return shape === '' ? '(empty)' : shape;
}
